import classnames from 'classnames'
import React, { useState } from 'react'
import { vn } from 'vend-number'
import { focusGlobalSearch } from '../../../ng-compat/hooks'
import { displayRound } from '../../../filters/display-round'
import { ERRORS } from '../../../constants'

interface Props {
  allowedPrefix?: string
  allowedSuffix?: string
  className: string
  error?: string | null
  decimalPlaces?: number
  min?: number | string
  max?: number | string
  onError: (errors: string | null) => void
  onValueChange: (value: string) => void
  trimInsignificantDigits?: boolean
  value: number | string
  onFocusChange: (value: string) => void
}

/**
 * Model -> View: Format given model input value and return the correct display value taking configured
 * options into account, e.g. decimalPlaces & trimInsignificant digits settings.
 *
 * @param input
 *        A vend number input value to format for display.
 *
 * @return The given vend number input formatted for display.
 */
const formatVendNumber = (
  input: number | string,
  {
    decimalPlaces,
    trimInsignificantDigits,
  }: Pick<Props, 'decimalPlaces' | 'trimInsignificantDigits'>
) => {
  let formattedNumber = input
  if (input != null) {
    formattedNumber = String(input)
  }

  if (formattedNumber) {
    formattedNumber = displayRound(
      formattedNumber,
      decimalPlaces,
      trimInsignificantDigits
    )
  }

  return formattedNumber
}

/**
 * Normalize given display input value (remove any allowed characters).
 *
 * There are special cases implemented for when the input is currently focused (i.e. we can assume from
 * this that the user is still typing) whereby certain usually invalid cases are interpreted as 0 for
 * example to ensure that calculations and validity do not break prematurely to a valid number being finished
 * (e.g. in cases of slow typing and such).
 *
 * @param input A number input value to parse and round for display.
 *
 * @return The normalized input.
 */
const normalizeNumber = (
  input: number | string,
  {
    allowedPrefix,
    allowedSuffix,
  }: Pick<Props, 'allowedPrefix' | 'allowedSuffix'>
) => {
  input = String(input).trim()
  let negative = false
  if (input.startsWith('-')) {
    negative = true
    input = input.slice(1)
  }

  if (allowedPrefix && input.startsWith(allowedPrefix)) {
    input = input.slice(allowedPrefix.length)
    // Allow negative to be either before or after the prefix.
    if (!negative && input.startsWith('-')) {
      negative = true
      input = input.slice(1)
    }
  }

  // allowedSuffix alone is invalid because you do not type suffix first.
  if (
    allowedSuffix &&
    input.length > allowedSuffix.length &&
    input.endsWith(allowedSuffix)
  ) {
    input = input.slice(0, -allowedSuffix.length)
  }

  // Special case to handle empty input as valid while typing when empty is not allowed.
  if (!input.trim() || input === '.') {
    input = '0'
  }

  if (negative && input !== '0') {
    return '-' + input
  }
  return input
}

/**
 * Ensure the value is a valid number. This is instead of setting the input to be a number, because this
 * doesn't allow us to enforce decimal places.
 *
 * Also enforce minimum and maximum, if specified.
 *
 * @param value
 *        The value to validate (has been parsed).
 *
 * @return Returns true if given value is a valid number, otherwise false.
 */
const validate = (
  value: string,
  { max, min }: Pick<Props, 'max' | 'min'>
): string | null => {
  const numberVal = Number(value)

  if (Number.isNaN(numberVal)) {
    return ERRORS.INVALID_CHARACTERS
  }
  if (min !== undefined && vn(numberVal).lt(min)) {
    return ERRORS.INVALID_MIN
  }
  if (max !== undefined && vn(numberVal).gt(max)) {
    return ERRORS.INVALID_MAX
  }
  return null
}

export const testables = { normalizeNumber }

const handleEnter: React.KeyboardEventHandler = event => {
  if (event.key === 'Enter') {
    focusGlobalSearch()
  }
}

/**
 * An input component used for line item quantity, price, and discount.
 */
export const LineItemInput = React.forwardRef(
  (props: Props, ref: React.Ref<HTMLInputElement>) => {
    const {
      className,
      error,
      onError,
      onValueChange,
      onFocusChange,
      value,
    } = props

    const [focused, setFocused] = useState(false)

    const [{ inputValue, validationError }, setInputValue] = useState(() => ({
      inputValue: formatVendNumber(value, props),
      validationError: null as string | null,
    }))

    let displayValue
    if (focused || validationError) {
      displayValue = inputValue
    } else {
      displayValue = formatVendNumber(value, props)
    }

    const onChange: React.ChangeEventHandler<HTMLInputElement> = event => {
      const inputValue = event.target.value

      const normalized = normalizeNumber(inputValue, props)
      const validationError = validate(normalized, props)
      setInputValue({ inputValue, validationError })
      if (validationError) {
        onError(validationError)
      } else {
        onError(null)
      }
      if (validationError !== ERRORS.INVALID_CHARACTERS) {
        onValueChange(normalized)
      }
    }

    const onBlur: React.FocusEventHandler = () => {
      setFocused(false)
      if (!validationError) {
        // Format normalized input value (eg remove '$', 2dp)
        const normalized = normalizeNumber(inputValue, props)
        const displayValue = formatVendNumber(normalized, props)
        setInputValue({
          inputValue: displayValue,
          validationError: null,
        })
        if (validationError !== ERRORS.INVALID_CHARACTERS) {
          onFocusChange(normalized)
        }
      }
    }

    const onFocus: React.FocusEventHandler = () => {
      setFocused(true)
      if (!validationError) {
        // Sync prop value to display value, in case someone changed it programmatically.
        const displayValue = formatVendNumber(value, props)
        setInputValue({
          inputValue: displayValue,
          validationError: null,
        })
      }
    }

    return (
      <input
        value={displayValue}
        className={classnames(
          'vd-input',
          'vd-input--text-align-right',
          { 'vd-error': validationError || error },
          className
        )}
        onBlur={onBlur}
        onFocus={onFocus}
        onChange={onChange}
        onKeyUp={handleEnter}
        ref={ref}
        autoComplete="off"
        inputMode="decimal"
        data-testid="line-item-input"
      />
    )
  }
)

LineItemInput.displayName = 'LineItemInput'
